import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/电影", label: "电影" },
  { to: "/category/电视剧", label: "电视剧" },
  { to: "/category/综艺", label: "综艺" },
  { to: "/category/动漫", label: "动漫" },
  { to: "/special", label: "专题" },
  { to: "/about", label: "关于我们" },
];

export default function SiteNavbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { favorites } = useFavorites();

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-movie-2-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <h1 className="font-heading text-lg font-extrabold tracking-tight text-primary-700">
              91免费看片
            </h1>
            <span className="mt-0.5 text-[10px] tracking-widest text-foreground-500">
              高清影视在线观看
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex flex-1 items-center justify-end gap-2">
          {/* Desktop search */}
          <form
            onSubmit={submitSearch}
            className="hidden items-center gap-1 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 md:flex"
          >
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称、演员…"
              className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-52"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
          </form>

          {/* Favorites */}
          <Link
            to="/favorites"
            className="relative hidden h-9 items-center gap-1 rounded-full border border-background-300 px-3 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 sm:flex"
          >
            <i className="ri-heart-3-line" />
            <span className="whitespace-nowrap">我的收藏</span>
            {favorites.length > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-background-50">
                {favorites.length}
              </span>
            )}
          </Link>

          {/* Mobile menu button */}
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称、演员…"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-100 text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/favorites"
              onClick={() => setMenuOpen(false)}
              className="col-span-2 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm font-medium text-accent-700"
            >
              <i className="ri-heart-3-line" /> 我的收藏（{favorites.length}）
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}